import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Modal, AppState, AccessibilityInfo, Dimensions, StyleSheet } from 'react-native'
import Animated, { useSharedValue, useAnimatedStyle, withDelay, withTiming, withSpring, withRepeat, withSequence, Easing } from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { hapticSuccess } from '../../lib/haptics'
import { resolvePerson } from '../../lib/personAvatar'
import { SPECIAL_BADGES } from '../personAvatarConfig'
import PersonAvatar from '../PersonAvatar'
import GradientButton from '../GradientButton'
import { useRouter } from 'expo-router'
import { tierColor, ROMAN } from '../../lib/badges'
import BadgeMedal from '../Badges/BadgeMedal'

// Overlay celebrativo: la prima volta che apri la Home dopo aver conquistato una maglia speciale (notifica 'jersey' non letta)
// o dei badge nuovi (notifica 'badge' di tipo 'earned', con tutti i badge in un'unica festa),
// l'avatar compare con la maglia, i coriandoli nei suoi colori e il testo della notifica. Alla chiusura la notifica
// risulta letta (e sparisce dal contatore del campanello). Una alla volta, solo se recenti.
const MAX_AGE_MS = 14 * 24 * 3600 * 1000
const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window')
const PIECES = 30

// Colori dei coriandoli di ogni maglia (gli stessi del badge sul volto, piu' il giallo delle stelle per l'europea)
const confettiColors = (code) => {
  const b = SPECIAL_BADGES[code]
  if (!b) return ['#FACC15', '#FFFFFF']
  return b.star ? [...b.colors, b.star, b.star] : b.colors
}

function localize(n, lang) {
  if (lang === 'en' && n.title_en) return { title: n.title_en, body: n.body_en }
  if (lang === 'es' && n.title_es) return { title: n.title_es, body: n.body_es }
  return { title: n.title, body: n.body }
}

function Piece({ i, color }) {
  const p = useSharedValue(0)
  // posizione, dimensione, ritardo e durata "casuali" ma stabili (derivati dall'indice): niente Math.random nel render
  const seed = (i * 9301 + 49297) % 233280 / 233280
  const x = (i / PIECES) * SCREEN_W + (seed - 0.5) * 40
  const size = 7 + Math.round(seed * 7)
  const delay = Math.round(((i * 37) % 11) * 90)
  const dur = 2400 + Math.round(((i * 53) % 9) * 180)
  const spin = (i % 2 ? 1 : -1) * (360 + Math.round(seed * 540))
  useEffect(() => { p.value = withDelay(delay, withTiming(1, { duration: dur, easing: Easing.out(Easing.quad) })) }, [])
  const style = useAnimatedStyle(() => ({
    opacity: p.value < 0.85 ? 1 : (1 - p.value) / 0.15,
    transform: [
      { translateY: -40 + p.value * (SCREEN_H + 60) },
      { translateX: Math.sin(p.value * 7 + i) * 18 },
      { rotate: `${p.value * spin}deg` },
    ],
  }))
  return <Animated.View pointerEvents="none" style={[{ position: 'absolute', top: 0, left: x, width: size, height: size * 1.6, backgroundColor: color, borderRadius: 2 }, style]} />
}

function Celebration({ n, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const { user, avatarSettings, language } = useAuth()
  const [reduceMotion, setReduceMotion] = useState(false)
  const code = n.meta?.jersey_code
  const colors = useMemo(() => confettiColors(code), [code])
  const person = useMemo(() => resolvePerson(avatarSettings, user?.id), [avatarSettings, user?.id])
  const { title, body } = localize(n, language)

  const scale = useSharedValue(0.5)
  const halo = useSharedValue(1)
  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled?.().then(setReduceMotion).catch(() => {})
    hapticSuccess()
    scale.value = withSpring(1, { damping: 9, stiffness: 110 })
    halo.value = withRepeat(withSequence(withTiming(1.08, { duration: 1100 }), withTiming(1, { duration: 1100 })), -1)
  }, [])
  const avatarStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }))
  const haloStyle = useAnimatedStyle(() => ({ transform: [{ scale: halo.value }] }))
  const main = colors[Math.min(1, colors.length - 1)]

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={onClose}>
      <View style={s.backdrop} accessibilityViewIsModal>
        {!reduceMotion && colors.length > 0 && Array.from({ length: PIECES }, (_, i) => <Piece key={i} i={i} color={colors[i % colors.length]} />)}

        <View style={s.center}>
          <View style={s.avatarWrap}>
            <Animated.View style={[s.halo, { backgroundColor: main + '33', borderColor: main + '88' }, haloStyle]} />
            <Animated.View style={avatarStyle}>
              <PersonAvatar person={person} size={240} special={code} />
            </Animated.View>
          </View>

          <Text style={s.title} accessibilityRole="header">{title}</Text>
          <Text style={s.body}>{body}</Text>

          <GradientButton onPress={onClose} size="lg" style={s.cta}>{t('jersey_celebration.ok')}</GradientButton>
        </View>
      </View>
    </Modal>
  )
}

// Festa dei badge: la medaglia del piu' importante in grande (livello piu' alto), le altre piccole sotto
function BadgeCelebration({ n, onClose }) {
  const { t } = useTranslation()
  const router = useRouter()
  const [reduceMotion, setReduceMotion] = useState(false)
  const list = n.meta?.badges ?? []
  const top = list[0]
  const color = tierColor(top?.level ?? 1, top?.max_level ?? 1)
  const colors = useMemo(() => [color, '#FFFFFF', '#FACC15'], [color])
  const others = list.slice(1, 6)
  const extra = Math.max(0, list.length - 1 - others.length)

  const scale = useSharedValue(0.4)
  const halo = useSharedValue(1)
  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled?.().then(setReduceMotion).catch(() => {})
    hapticSuccess()
    scale.value = withSpring(1, { damping: 8, stiffness: 110 })
    halo.value = withRepeat(withSequence(withTiming(1.08, { duration: 1100 }), withTiming(1, { duration: 1100 })), -1)
  }, [])
  const medalStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }))
  const haloStyle = useAnimatedStyle(() => ({ transform: [{ scale: halo.value }] }))
  if (!top) return null

  const name = (b) => b.max_level > 1 ? `${t(`badges.fam.${b.category}.title`)} ${ROMAN[b.level - 1] ?? b.level}` : t(`badges.fam.${b.category}.title`)

  return (
    <Modal visible transparent animationType="fade" statusBarTranslucent onRequestClose={onClose}>
      <View style={s.backdrop} accessibilityViewIsModal>
        {!reduceMotion && Array.from({ length: PIECES }, (_, i) => <Piece key={i} i={i} color={colors[i % colors.length]} />)}
        <View style={s.center}>
          <View style={s.avatarWrap}>
            <Animated.View style={[s.halo, { backgroundColor: color + '33', borderColor: color + '88' }, haloStyle]} />
            <Animated.View style={medalStyle}>
              <BadgeMedal icon={top.icon} color={color} size={170} />
            </Animated.View>
          </View>
          <Text style={s.kicker}>{list.length > 1 ? t('badges.celebration_many', { n: list.length }) : t('badges.celebration_one')}</Text>
          <Text style={s.title} accessibilityRole="header">{name(top)}</Text>
          <Text style={s.body}>{t(`badges.fam.${top.category}.desc`, { n: top.threshold })}</Text>
          {others.length > 0 && (
            <View style={s.others}>
              {others.map(b => <BadgeMedal key={b.code} icon={b.icon} color={tierColor(b.level, b.max_level)} size={46} />)}
              {extra > 0 && <Text style={s.more}>{t('badges.celebration_more', { n: extra })}</Text>}
            </View>
          )}
          <GradientButton onPress={onClose} size="lg" style={s.cta}>{t('jersey_celebration.ok')}</GradientButton>
          <Text onPress={() => { onClose(); router.push('/Leagues/badges') }} accessibilityRole="button" style={s.link}>{t('badges.open_album')}</Text>
        </View>
      </View>
    </Modal>
  )
}

export default function JerseyCelebration({ enabled = true }) {
  const { user } = useAuth()
  const [queue, setQueue] = useState([])
  const loading = useRef(false)

  const load = useCallback(async () => {
    if (!user?.id || loading.current) return
    loading.current = true
    try {
      const { data } = await supabase.rpc('fn_get_notifications', { p_id_user: user.id, p_limit: 20 })
      const fresh = (data ?? [])
        .filter(n => !n.is_read && Date.now() - new Date(n.created_at).getTime() < MAX_AGE_MS
          && ((n.type === 'jersey' && n.meta?.jersey_code) || (n.type === 'badge' && n.meta?.kind === 'earned' && n.meta?.badges?.length)))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      setQueue(prev => (prev.length ? prev : fresh))
    } catch { /* l'overlay e' accessorio: nessun errore all'utente */ }
    finally { loading.current = false }
  }, [user?.id])

  useEffect(() => {
    if (!enabled) return
    load()
    const sub = AppState.addEventListener('change', st => { if (st === 'active') load() })
    return () => sub.remove()
  }, [enabled, load])

  const current = enabled ? queue[0] : null
  if (!current) return null

  const close = async () => {
    setQueue(q => q.slice(1))
    // la notifica risulta letta: sparisce dal contatore del campanello
    try { await supabase.from('fact_notification_read').insert({ id_notification: current.id, id_user: user.id }) } catch {}
  }
  return current.type === 'badge'
    ? <BadgeCelebration key={current.id} n={current} onClose={close} />
    : <Celebration key={current.id} n={current} onClose={close} />
}

const s = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(8,4,10,0.86)', overflow: 'hidden' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 28, gap: 14 },
  avatarWrap: { width: 260, height: 260, alignItems: 'center', justifyContent: 'center', marginBottom: 6 },
  halo: { position: 'absolute', width: 250, height: 250, borderRadius: 125, borderWidth: 2 },
  kicker: { color: 'rgba(255,255,255,0.7)', fontFamily: fonts.condensedBold, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase' },
  others: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 },
  more: { color: 'rgba(255,255,255,0.86)', fontSize: 14, fontFamily: fonts.condensedBold },
  link: { color: '#FFFFFF', fontSize: 14, fontFamily: fonts.condensedBold, minHeight: 44, textAlignVertical: 'center', textDecorationLine: 'underline' },
  title: { color: '#FFFFFF', fontFamily: fonts.condensedBold, fontSize: 34, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5 },
  body: { color: 'rgba(255,255,255,0.86)', fontSize: 15, lineHeight: 22, textAlign: 'center', maxWidth: 340 },
  cta: { alignSelf: 'stretch', marginTop: 10, minWidth: 220 },
})
